import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, LogOut, Loader2, XCircle, Mail, Users, Briefcase,
  FileText, BookOpen, FolderKanban, Plus, Pencil, Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (s) =>
  new Date(s).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const statusBadge = (status) => ({
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-green-100 text-green-700',
}[status] || 'bg-gray-100 text-gray-700');

// --------------------------- helpers ---------------------------

function useAuthAxios(token) {
  return useMemo(() => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    return {
      get: (url) => axios.get(`${API}${url}`, config),
      post: (url, body) => axios.post(`${API}${url}`, body, config),
      patch: (url, body, params) =>
        axios.patch(`${API}${url}`, body, { ...config, params }),
      delete: (url) => axios.delete(`${API}${url}`, config),
    };
  }, [token]);
}

// --------------------------- Submissions tab ---------------------------

const SubmissionsTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/submissions');
      setRows(data);
    } catch (e) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/submissions/${id}/status`, null, { status: newStatus });
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (rows.length === 0) return <EmptyState icon={Mail} message="No contact submissions yet" />;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Project Type</TableHead>
            <TableHead>Budget</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id}>
              <TableCell className="font-medium">{s.name}</TableCell>
              <TableCell>{s.company}</TableCell>
              <TableCell>
                <a href={`mailto:${s.email}`} className="text-primary hover:underline">{s.email}</a>
              </TableCell>
              <TableCell>{s.project_type}</TableCell>
              <TableCell>{s.budget || '-'}</TableCell>
              <TableCell className="text-sm text-text-muted">{formatDate(s.submitted_at)}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(s.status)}`}>
                  {s.status.replace('_', ' ')}
                </span>
              </TableCell>
              <TableCell>
                <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
                  <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// --------------------------- Leads tab ---------------------------

const LeadsTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leads')
      .then(({ data }) => setRows(data))
      .catch(() => toast.error('Failed to load leads'))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (rows.length === 0) return <EmptyState icon={Users} message="No hiring leads yet" />;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Talents</TableHead>
            <TableHead>Roles</TableHead>
            <TableHead>Budget</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((l) => (
            <TableRow key={l.id}>
              <TableCell className="font-medium">{l.full_name}</TableCell>
              <TableCell>
                <a href={`mailto:${l.work_email}`} className="text-primary hover:underline">{l.work_email}</a>
              </TableCell>
              <TableCell>{l.phone || '-'}</TableCell>
              <TableCell>{l.talents_required}</TableCell>
              <TableCell>{l.roles_required}</TableCell>
              <TableCell>{l.budget}</TableCell>
              <TableCell className="text-sm text-text-muted">{formatDate(l.submitted_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// --------------------------- Career applications tab ---------------------------

const CareerAppsTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/career-applications')
      .then(({ data }) => setRows(data))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (rows.length === 0) return <EmptyState icon={Briefcase} message="No job applications yet" />;

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Job</TableHead>
            <TableHead>Message</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Resume</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="font-medium">{a.full_name}</TableCell>
              <TableCell>
                <a href={`mailto:${a.work_email}`} className="text-primary hover:underline">{a.work_email}</a>
              </TableCell>
              <TableCell>{a.phone}</TableCell>
              <TableCell>{a.job_title || a.job_id}</TableCell>
              <TableCell className="max-w-xs truncate">{a.message || '-'}</TableCell>
              <TableCell className="text-sm text-text-muted">{formatDate(a.submitted_at)}</TableCell>
              <TableCell>
                {a.resume_path ? (
                  <span className="text-xs text-text-muted">{a.resume_path.split('/').pop()}</span>
                ) : (
                  <span className="text-text-muted">-</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// --------------------------- Shared modal form ---------------------------

const FormDialog = ({ open, onOpenChange, title, fields, initial, onSave, saving }) => {
  const [values, setValues] = useState(initial || {});

  useEffect(() => { setValues(initial || {}); }, [initial, open]);

  const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const submit = (e) => {
    e.preventDefault();
    onSave(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-2">
              <Label htmlFor={f.key}>{f.label}{f.required && ' *'}</Label>
              {f.type === 'textarea' ? (
                <Textarea
                  id={f.key}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  required={f.required}
                  rows={f.rows || 4}
                  placeholder={f.placeholder}
                />
              ) : f.type === 'list' ? (
                <Textarea
                  id={f.key}
                  value={Array.isArray(values[f.key]) ? values[f.key].join('\n') : (values[f.key] ?? '')}
                  onChange={(e) => setField(f.key, e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                  rows={4}
                  placeholder={f.placeholder || 'One item per line'}
                />
              ) : f.type === 'switch' ? (
                <div className="flex items-center space-x-2">
                  <Switch
                    id={f.key}
                    checked={values[f.key] ?? false}
                    onCheckedChange={(v) => setField(f.key, v)}
                  />
                  <span className="text-sm text-text-secondary">
                    {values[f.key] ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ) : (
                <Input
                  id={f.key}
                  type={f.type || 'text'}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  required={f.required}
                  placeholder={f.placeholder}
                />
              )}
              {f.help && <p className="text-xs text-text-muted">{f.help}</p>}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-primary text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------- Blog tab ---------------------------

const BLOG_FIELDS = [
  { key: 'title', label: 'Title', required: true },
  { key: 'slug', label: 'Slug', required: true, help: 'URL-friendly identifier (lowercase, hyphens, no spaces)' },
  { key: 'excerpt', label: 'Excerpt', type: 'textarea', rows: 2, required: true, help: 'Short summary shown on the blog index page' },
  { key: 'content', label: 'Content (Markdown supported)', type: 'textarea', rows: 10, required: true },
  { key: 'author', label: 'Author', required: true },
  { key: 'tags', label: 'Tags', type: 'list', help: 'One tag per line' },
  { key: 'image_url', label: 'Cover Image URL', placeholder: 'https://...' },
];

const BlogTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/blog`);
      setRows(data);
    } catch {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setOpen(true); };

  const save = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/blog/${editing.id}`, values);
        toast.success('Blog post updated');
      } else {
        await api.post('/blog', values);
        toast.success('Blog post created');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this blog post permanently?')) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success('Blog post deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-muted">{rows.length} post{rows.length !== 1 ? 's' : ''}</p>
        <Button onClick={openNew} className="bg-primary text-white"><Plus className="h-4 w-4 mr-1" /> New Post</Button>
      </div>
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={BookOpen} message="No blog posts yet — create one with the button above" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Published</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.id}>
                <TableCell className="font-medium">{b.title}</TableCell>
                <TableCell className="text-xs text-text-muted">{b.slug}</TableCell>
                <TableCell>{b.author}</TableCell>
                <TableCell className="text-sm text-text-muted">{formatDate(b.published_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(b)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(b.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Edit Blog Post' : 'New Blog Post'}
        fields={BLOG_FIELDS}
        initial={editing}
        onSave={save}
        saving={saving}
      />
    </div>
  );
};

// --------------------------- Case Studies tab ---------------------------

const CASE_FIELDS = [
  { key: 'title', label: 'Title', required: true },
  { key: 'slug', label: 'Slug', required: true },
  { key: 'client_name', label: 'Client Name', required: true },
  { key: 'industry', label: 'Industry', required: true },
  { key: 'problem', label: 'Problem Statement', type: 'textarea', rows: 4, required: true },
  { key: 'solution', label: 'Solution', type: 'textarea', rows: 6, required: true },
  { key: 'technologies', label: 'Technologies', type: 'list', help: 'One technology per line' },
  { key: 'image_url', label: 'Cover Image URL' },
  { key: 'architecture_description', label: 'Architecture (optional)', type: 'textarea', rows: 3 },
];

const CaseStudiesTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/case-studies`);
      setRows(data);
    } catch {
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    setSaving(true);
    try {
      // results field defaults to empty array if not provided (backend requires it)
      const payload = { ...values, results: values.results || [] };
      if (editing) {
        await api.patch(`/case-studies/${editing.id}`, payload);
        toast.success('Case study updated');
      } else {
        await api.post('/case-studies', payload);
        toast.success('Case study created');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this case study permanently?')) return;
    try {
      await api.delete(`/case-studies/${id}`);
      toast.success('Case study deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-muted">{rows.length} case stud{rows.length !== 1 ? 'ies' : 'y'}</p>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary text-white"><Plus className="h-4 w-4 mr-1" /> New Case Study</Button>
      </div>
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={FolderKanban} message="No case studies yet — create one with the button above" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Published</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.title}</TableCell>
                <TableCell>{c.client_name}</TableCell>
                <TableCell>{c.industry}</TableCell>
                <TableCell className="text-sm text-text-muted">{formatDate(c.published_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Edit Case Study' : 'New Case Study'}
        fields={CASE_FIELDS}
        initial={editing}
        onSave={save}
        saving={saving}
      />
    </div>
  );
};

// --------------------------- Jobs tab ---------------------------

const JOB_FIELDS = [
  { key: 'title', label: 'Job Title', required: true, placeholder: 'e.g. Senior React Developer' },
  { key: 'slug', label: 'Slug', required: true, placeholder: 'e.g. senior-react-developer' },
  { key: 'description', label: 'Description', type: 'textarea', rows: 4, required: true },
  { key: 'requirements', label: 'Requirements', type: 'list', help: 'One requirement per line' },
  { key: 'responsibilities', label: 'Responsibilities', type: 'list', help: 'One responsibility per line' },
  { key: 'location', label: 'Location', required: true, placeholder: 'e.g. Remote / Bangalore, India' },
  { key: 'job_type', label: 'Job Type', required: true, placeholder: 'Full-time / Part-time / Contract' },
  { key: 'experience_level', label: 'Experience Level', required: true, placeholder: 'e.g. Mid (3-5 yrs)' },
  { key: 'salary_range', label: 'Salary Range (optional)', placeholder: 'e.g. $80k - $120k' },
  { key: 'is_active', label: 'Active (visible on /careers)', type: 'switch' },
];

const JobsTab = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/jobs');
      setRows(data);
    } catch {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        requirements: values.requirements || [],
        responsibilities: values.responsibilities || [],
        is_active: values.is_active !== false,
      };
      if (editing) {
        await api.patch(`/jobs/${editing.id}`, payload);
        toast.success('Job updated');
      } else {
        await api.post('/jobs', payload);
        toast.success('Job created');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (job) => {
    try {
      await api.patch(`/jobs/${job.id}`, { is_active: !job.is_active });
      load();
    } catch {
      toast.error('Failed to toggle');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this job posting permanently?')) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success('Job deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-muted">{rows.length} job{rows.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-primary text-white"><Plus className="h-4 w-4 mr-1" /> New Job</Button>
      </div>
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Briefcase} message="No job openings yet — create one with the button above" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((j) => (
              <TableRow key={j.id}>
                <TableCell className="font-medium">{j.title}</TableCell>
                <TableCell>{j.location}</TableCell>
                <TableCell>{j.job_type}</TableCell>
                <TableCell>
                  <Switch checked={j.is_active} onCheckedChange={() => toggleActive(j)} />
                </TableCell>
                <TableCell className="text-sm text-text-muted">{formatDate(j.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(j); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(j.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Edit Job' : 'New Job'}
        fields={JOB_FIELDS}
        initial={editing || { is_active: true }}
        onSave={save}
        saving={saving}
      />
    </div>
  );
};

// --------------------------- Empty state ---------------------------

const EmptyState = ({ icon: Icon, message }) => (
  <div className="p-12 text-center">
    <Icon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
    <p className="text-text-muted">{message}</p>
  </div>
);

// --------------------------- Top-level page ---------------------------

const AdminPage = () => {
  const { user, token, logout, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const api = useAuthAxios(token);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [authLoading, user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return null;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <XCircle className="h-16 w-16 text-red-500 mb-4" />
        <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">Access Denied</h1>
        <p className="text-text-secondary mb-6">You don't have admin permission.</p>
        <Button onClick={() => navigate('/')} className="bg-primary text-white rounded-full">Go Home</Button>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <LayoutDashboard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-text-primary">Admin Dashboard</h1>
              <p className="text-sm text-text-muted">Welcome, {user.name}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="rounded-full" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="submissions" className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <TabsList className="flex flex-wrap gap-2 mb-6 h-auto bg-slate-100">
            <TabsTrigger value="submissions"><Mail className="h-4 w-4 mr-1" /> Submissions</TabsTrigger>
            <TabsTrigger value="leads"><Users className="h-4 w-4 mr-1" /> Leads</TabsTrigger>
            <TabsTrigger value="careers"><Briefcase className="h-4 w-4 mr-1" /> Applications</TabsTrigger>
            <TabsTrigger value="blog"><BookOpen className="h-4 w-4 mr-1" /> Blog</TabsTrigger>
            <TabsTrigger value="case-studies"><FolderKanban className="h-4 w-4 mr-1" /> Case Studies</TabsTrigger>
            <TabsTrigger value="jobs"><FileText className="h-4 w-4 mr-1" /> Jobs</TabsTrigger>
          </TabsList>

          <TabsContent value="submissions"><SubmissionsTab api={api} /></TabsContent>
          <TabsContent value="leads"><LeadsTab api={api} /></TabsContent>
          <TabsContent value="careers"><CareerAppsTab api={api} /></TabsContent>
          <TabsContent value="blog"><BlogTab api={api} /></TabsContent>
          <TabsContent value="case-studies"><CaseStudiesTab api={api} /></TabsContent>
          <TabsContent value="jobs"><JobsTab api={api} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
